import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { canonicalMoney } from '../lib/money';
import type { PayPeriodRow, PayrollTotals } from '../types';

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function tsToHours(iso: string): number {
  const t = iso?.includes('T') ? iso.split('T')[1] : iso ?? '0:0';
  const [h, m] = t.split(':').map(Number);
  return h + m / 60;
}

interface UsePayrollResult {
  payPeriodData: PayPeriodRow[];
  totals: PayrollTotals;
  weekStart: Date;
  weekEnd: Date;
  setWeek: (date: Date) => void;
  exportCSV: () => void;
  loading: boolean;
  error: string | null;
}

export function usePayroll(businessId: string | undefined, otThreshold: number): UsePayrollResult {
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => startOfWeek(new Date()));
  const [payPeriodData, setPayPeriodData] = useState<PayPeriodRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const weekStart = useMemo(() => startOfWeek(weekAnchor), [weekAnchor]);
  const weekEnd = useMemo(() => {
    const e = new Date(weekStart);
    e.setDate(e.getDate() + 6);
    return e;
  }, [weekStart]);

  const fetchPayroll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    const startStr = formatDate(weekStart);
    const endStr = formatDate(weekEnd);

    try {
      const [usersRes, shiftsRes] = await Promise.all([
        supabase
          .from('users')
          .select('id, first_name, last_name, employment_type, hourly_rate, status')
          .eq('business_id', businessId)
          .eq('status', 'active'),

        supabase
          .from('schedule_entries')
          .select('assigned_to, start_at, end_at, status')
          .eq('business_id', businessId)
          .eq('type', 'shift')
          .gte('start_at', `${startStr}T00:00:00`)
          .lte('start_at', `${endStr}T23:59:59`)
          .neq('status', 'cancelled'),
      ]);

      const users = usersRes.data ?? [];
      const shifts = shiftsRes.data ?? [];

      // Group entries by user (assigned_to is an array)
      const shiftsByUser = new Map<string, typeof shifts>();
      for (const s of shifts) {
        const uid = Array.isArray(s.assigned_to) ? s.assigned_to[0] : null;
        if (!uid) continue;
        const arr = shiftsByUser.get(uid) ?? [];
        arr.push(s);
        shiftsByUser.set(uid, arr);
      }

      const rows: PayPeriodRow[] = users.map(u => {
        const userShifts = shiftsByUser.get(u.id) ?? [];
        let scheduled = 0;
        let actual = 0;
        for (const s of userShifts) {
          const schedH = Math.max(0, tsToHours(s.end_at) - tsToHours(s.start_at));
          scheduled += schedH;
          actual += schedH; // schedule_entries doesn't track actual vs scheduled separately
        }

        const rate = u.hourly_rate ?? 0;
        const regular = Math.min(actual, otThreshold);
        const ot = Math.max(0, actual - otThreshold);
        const gross = regular * rate + ot * rate * 1.5;

        return {
          userId: u.id,
          name: `${u.first_name} ${u.last_name}`,
          employmentType: u.employment_type,
          scheduledHours: Math.round(scheduled * 100) / 100,
          actualHours: Math.round(actual * 100) / 100,
          regularHours: Math.round(regular * 100) / 100,
          overtimeHours: Math.round(ot * 100) / 100,
          hourlyRate: rate,
          grossPay: Math.round(gross * 100) / 100,
          otWarning: actual >= (otThreshold - 8), // warn if within 8h of threshold
        };
      });

      rows.sort((a, b) => b.actualHours - a.actualHours);
      setPayPeriodData(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payroll');
    } finally {
      setLoading(false);
    }
  }, [businessId, weekStart, weekEnd, otThreshold]);

  useEffect(() => { void fetchPayroll(); }, [fetchPayroll]);

  const totals = useMemo<PayrollTotals>(() => {
    return payPeriodData.reduce((acc, r) => ({
      scheduledHours: acc.scheduledHours + r.scheduledHours,
      actualHours: acc.actualHours + r.actualHours,
      regularHours: acc.regularHours + r.regularHours,
      overtimeHours: acc.overtimeHours + r.overtimeHours,
      grossPay: acc.grossPay + r.grossPay,
    }), { scheduledHours: 0, actualHours: 0, regularHours: 0, overtimeHours: 0, grossPay: 0 });
  }, [payPeriodData]);

  const exportCSV = useCallback(() => {
    const header = 'Name,Type,Scheduled Hours,Actual Hours,Regular,Overtime,Rate,Gross Pay';
    const rows = payPeriodData.map(r =>
      `"${r.name}",${r.employmentType},${r.scheduledHours},${r.actualHours},${r.regularHours},${r.overtimeHours},${r.hourlyRate},${r.grossPay}`
    );
    // Hours stay number-formatted; grossPay is money.
    const totalRow = `"TOTAL",,${totals.scheduledHours.toFixed(2)},${totals.actualHours.toFixed(2)},${totals.regularHours.toFixed(2)},${totals.overtimeHours.toFixed(2)},,${canonicalMoney(totals.grossPay)}`;
    const csv = [header, ...rows, totalRow].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `crewbit-payroll-${formatDate(weekStart)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [payPeriodData, totals, weekStart]);

  return { payPeriodData, totals, weekStart, weekEnd, setWeek: setWeekAnchor, exportCSV, loading, error };
}
